import { ChangeDetectionStrategy, Component, Injector, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { debounce, takeUntil } from 'rxjs/operators';
import { interval } from 'rxjs';
import { FiContactInformationTypes } from '@fi-shared/types/fi-contact-information-types';
import { IContactModel } from '@fi-shared/models/common';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FiContactsBaseDirective } from '../fi-contacts-base.directive';
import { DataFieldPropertyType } from '@fi-app/data-field/data-field-property-type';

@Component({
    selector: 'fi-phone-number-contacts',
    templateUrl: './phone-number-contacts.component.html',
    styleUrls: ['./phone-number-contacts.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-phone-number-contacts' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: PhoneNumberContactsComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: PhoneNumberContactsComponent,
        },
    ],
    standalone: false,
})
export class PhoneNumberContactsComponent extends FiContactsBaseDirective implements OnInit {
    @Input() isEditDisabled: boolean;
    @Input() isCountryCodeNewLayout: boolean;
    @Input() userId: number;

    easyRequestAccessMode: boolean = false; // note: is used in template but had no declaration
    dataFieldPropertyType = DataFieldPropertyType.PhoneNumber;

    get phoneNumbers(): IContactModel[] {
        return this.ngModel;
    }

    constructor(
        injector: Injector,
        private appTextService: AppTextService,
        private fiBrowserEventsService: FiBrowserEventsService,
    ) {
        super(injector);
    }

    ngOnInit() {
        super.ngOnInit();
        this.subscribeOnResize();
    }

    getContactTypesFn: () => FiContactInformationTypes[] = () => {
        return this.contactService.getPhoneNumberTypes();
    };

    isContactSignInTypeFn: () => boolean = () => {
        return this.fiUserSignInSettings?.isPhoneNumberSignInType();
    };

    getContactTypeName: (contactType: FiContactInformationTypes) => string = (
        contactType: FiContactInformationTypes,
    ) => {
        switch (contactType) {
            case FiContactInformationTypes.CellPhoneNumber:
                return this.appTextService.get('common.mobile');
            case FiContactInformationTypes.WorkPhoneNumber:
                return this.appTextService.get('common.work');
            case FiContactInformationTypes.HomePhoneNumber:
                return this.appTextService.get('common.home');
            case FiContactInformationTypes.OtherPhoneNumber:
                return this.appTextService.get('common.other');
            default:
                return this.contactService.getContactTypeName(contactType);
        }
    };

    private subscribeOnResize() {
        this.fiBrowserEventsService
            .getWindowEventSubject$('resize')
            .pipe(
                debounce(() => interval(200)),
                takeUntil(this.ngOnDestroy$),
            )
            .subscribe(() => this.cdr.markForCheck());
    }

    getNextNewContactTypeFn: () => FiContactInformationTypes = () => {
        if (!this.isContactTypeChosen(FiContactInformationTypes.CellPhoneNumber))
            return FiContactInformationTypes.CellPhoneNumber;

        if (!this.isContactTypeChosen(FiContactInformationTypes.WorkPhoneNumber))
            return FiContactInformationTypes.WorkPhoneNumber;

        if (!this.isContactTypeChosen(FiContactInformationTypes.HomePhoneNumber))
            return FiContactInformationTypes.HomePhoneNumber;

        return FiContactInformationTypes.OtherPhoneNumber;
    };
    isContactActionShownForScreen(isMobile: boolean): boolean {
        return this.isMobileSmall() === isMobile && !this.easyRequestAccessMode;
    }
}
